import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";
import { Form, Formik } from "formik";
import SelectField from "./Fields/SelectField";
import InputField from "./Fields/InputField";
import { createUserAccountsValidation } from "../../helpers/validationSchemes";
import { PDFDocument, rgb } from "pdf-lib";
import { saveAs } from "file-saver";
import { Project } from "../../types/project";
import { createAppUsers } from "../../api/project";
import { useApplicationContext } from "../ContextProviders/ApplicationContext";

type CreateUserAccountsPopupProps = {
  isDialogOpen: boolean;
  handleClose: () => void;
  project: Project;
};

const languages = [
  { label: "Dansk", value: "da" },
  { label: "English", value: "en" },
  { label: "Norsk", value: "nb" },
  { label: "Deutsch", value: "de" },
];

export default function CreateUserAccountsPopup({
  isDialogOpen,
  handleClose,
  project,
}: CreateUserAccountsPopupProps) {
  const { showLoading, isLoading, stopLoading } = useApplicationContext();

  const handleSubmit = async (values: {
    numberOfAccounts: number;
    language: string;
  }) => {
    try {
      showLoading("");
      await generatePDF(values.numberOfAccounts, values.language);
    } catch (e) {
    } finally {
      stopLoading();
      handleClose();
    }
  };

  const generatePDF = async (users: number, language: string) => {
    // Fetch the existing PDF (replace with your actual file path)
    const existingPdfBytes = await fetch(
      `https://precure-cdn.azureedge.net/files/mli-interface/slippers/back/Onboarding_slipper_${language}.pdf`
    ).then((res) => res.arrayBuffer());

    // Load the PDF
    const pdfDoc = await PDFDocument.load(existingPdfBytes);

    // Get new users

    const usersData = await createAppUsers(
      project.projectCode,
      users,
      new AbortController()
    );

    const newPdfDoc = await PDFDocument.create();

    for (let i = 0; i < users; i++) {
      const [clonedFirstPage, clonedSecondPage] = await newPdfDoc.copyPages(
        pdfDoc,
        [0, 1]
      );

      // Add login information (adjust coordinates accordingly)
      clonedFirstPage.drawText(usersData[i % usersData.length].username, {
        x: 160,
        y: 115,
        size: 22,
        color: rgb(0, 0, 0),
      });
      clonedFirstPage.drawText(usersData[i % usersData.length].password, {
        x: 160,
        y: 75,
        size: 22,
        color: rgb(0, 0, 0),
      });
      clonedFirstPage.drawText(project.projectCode, {
        x: 160,
        y: 40,
        size: 22,
        color: rgb(0, 0, 0),
      });

      // Add both cloned pages to the new PDF document
      newPdfDoc.addPage(clonedFirstPage);
      newPdfDoc.addPage(clonedSecondPage);
    }

    // Serialize the modified PDF
    const pdfBytes = await newPdfDoc.save();

    // Trigger download
    const blob = new Blob([pdfBytes], { type: "application/pdf" });

    const currentDate = new Date().toISOString().split("T")[0]; // Format: YYYY-MM-DD
    saveAs(blob, `${project.projectCode}_${currentDate}.pdf`);
  };

  return (
    <Dialog open={isDialogOpen} onClose={handleClose}>
      <DialogTitle id="alert-dialog-title">Generate user accounts</DialogTitle>
      <Formik
        initialValues={{ numberOfAccounts: 0, language: "da" }}
        onSubmit={handleSubmit}
        validateOnMount
        validationSchema={createUserAccountsValidation}
      >
        {({
          values,
          setFieldValue,
          getFieldProps,
          resetForm,
          isValid,
          dirty,
        }) => (
          <Form>
            <DialogContent>
              <Grid container spacing={2} flexDirection={"column"}>
                <Grid item xs>
                  <InputField
                    disabled={isLoading}
                    inputProps={{ inputMode: "numeric" }}
                    sx={{ marginTop: 2 }}
                    label="Number of accounts"
                    name="numberOfAccounts"
                  />
                </Grid>
                <Grid item xs>
                  <SelectField
                    disabled={isLoading}
                    name="language"
                    fullWidth
                    data={languages.sort((a, b) => {
                      if (a.label < b.label) return -1;
                      if (a.label > b.label) return 1;
                      return 0;
                    })}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Grid container justifyContent={"space-between"} display={"flex"}>
                <Grid item>
                  <Button
                    disabled={isLoading}
                    variant="contained"
                    type="submit"
                  >
                    Generate
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    disabled={isLoading}
                    aria-label="close"
                    onClick={handleClose}
                  >
                    Close
                  </Button>
                </Grid>
              </Grid>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
